import React, { useState, useCallback, useEffect } from "react";
import useStyles from "./style";
import { Button, Grid, Box } from "@mui/material";
import {
  ContentCopyOutlined,
  TableChartOutlined,
  DescriptionOutlined,
  PictureAsPdfOutlined,
  FileDownloadOutlined,
  Visibility,
  Delete,
} from "@mui/icons-material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch } from "react-redux";
import * as XLSX from "xlsx";
import Papa from "papaparse";
import jsPDF from "jspdf";
import "jspdf-autotable";
import _ from "lodash";
import ConversationApi from "Services/Conversation";
import ConversationViewModal from "Pages/Modals/Conversation/View";
import ConversationDeleteModal from "Pages/Modals/Conversation/Delete";

const Conversation = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [searchText, setSearchText] = useState("");
  const [row, setRow] = useState([]);
  const [bulkrow, setBulkrow] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [viewConversationModal, setViewConversationModal] = useState(false);
  const [deleteConversationModal, setDeleteConversationModal] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState("");

  const normalizeConversationList = (res) => {
    const payload = res?.data;
    if (Array.isArray(payload?.conversations)) {
      return { rows: payload.conversations || [], total: payload?.total || payload?.conversations?.length || 0 };
    }
    if (Array.isArray(payload?.data?.conversations)) {
      return { rows: payload.data.conversations || [], total: payload?.data?.total || payload?.data?.conversations?.length || 0 };
    }
    return { rows: [], total: 0 };
  };

  const getConversations = useCallback(() => {
    dispatch(showLoader("Loading please wait..."));
    ConversationApi.getConversationList({ page, limit: pageSize, search: searchText }).then(
      (res) => {
        dispatch(hideLoader());
        const normalized = normalizeConversationList(res);
        setTotalPages(normalized.total || 1);
        setRow(normalized.rows || []);
      },
      (err) => {
        dispatch(hideLoader());
        dispatch(
          showSnackbar({
            message: err?.response?.data?.message || "Failed to fetch data",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "error",
          })
        );
      }
    );
  }, [dispatch, page, pageSize, searchText]);

  const getAllConversations = useCallback(() => {
    ConversationApi.getConversationList({ page: 1, limit: "unlimited", search: "" }).then(
      (res) => {
        const normalized = normalizeConversationList(res);
        setBulkrow(normalized.rows || []);
      },
      () => setBulkrow([])
    );
  }, []);

  useEffect(() => {
    getConversations();
    getAllConversations();
  }, [getConversations, getAllConversations]);

  const calculateSno = (pageIndex, pageLimit, index) => (pageIndex - 1) * pageLimit + index + 1;

  const dataRows = (row || []).map((data, index) => {
    const lastMessage = Array.isArray(data?.messages) && data.messages.length ? data.messages[data.messages.length - 1] : null;
    return {
      id: data?.id || index + 1,
      sno: calculateSno(page, pageSize, index),
      conversationId: data?.id || "-",
      bookingId: data?.booking_id || data?.bookings?.id || "-",
      rideId: data?.ride_id || data?.rides?.id || "-",
      bookingStatus: data?.bookings?.status || "-",
      rideStatus: data?.rides?.status || "-",
      messagesCount: Array.isArray(data?.messages) ? data.messages.length : 0,
      lastMessage: lastMessage?.body || "-",
      lastMessageAt: lastMessage?.created_at ? new Date(lastMessage.created_at).toLocaleString() : "-",
      createdAt: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
      raw: data,
    };
  });

  const formatted = dataRows.map((r) => ({
    "S.NO": r.sno,
    "Conversation ID": r.conversationId,
    "Booking ID": r.bookingId,
    "Ride ID": r.rideId,
    "Booking Status": r.bookingStatus,
    "Ride Status": r.rideStatus,
    "Messages Count": r.messagesCount,
    "Last Message": r.lastMessage,
    "Last Message At": r.lastMessageAt,
    Created: r.createdAt,
  }));

  const bulkFormatted = _.orderBy(bulkrow || [], ["created_at"], ["asc"]).map((data, index) => {
    const lastMessage = Array.isArray(data?.messages) && data.messages.length ? data.messages[data.messages.length - 1] : null;
    return {
      "S.NO": index + 1,
      "Conversation ID": data?.id || "-",
      "Booking ID": data?.booking_id || data?.bookings?.id || "-",
      "Ride ID": data?.ride_id || data?.rides?.id || "-",
      "Booking Status": data?.bookings?.status || "-",
      "Ride Status": data?.rides?.status || "-",
      "Messages Count": Array.isArray(data?.messages) ? data.messages.length : 0,
      "Last Message": lastMessage?.body || "-",
      "Last Message At": lastMessage?.created_at ? new Date(lastMessage.created_at).toLocaleString() : "-",
      Created: data?.created_at ? new Date(data.created_at).toLocaleString() : "-",
    };
  });

  function CustomToolbar() {
    return (
      <Box sx={{ p: 1, pb: 0, display: "flex", justifyContent: "flex-start" }}>
        <GridToolbarQuickFilter debounceMs={800} autoFocus />
        <GridToolbarContainer>
          <GridToolbarDensitySelector />
        </GridToolbarContainer>
      </Box>
    );
  }

  const handleView = (conversationRow) => {
    const conversationId = conversationRow?.id || conversationRow?._id;
    if (!conversationId) {
      dispatch(
        showSnackbar({
          message: "Conversation ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedConversationId(conversationId);
    setViewConversationModal(true);
  };
  const handleDelete = (conversationRow) => {
    const conversationId = conversationRow?.id || conversationRow?._id;
    if (!conversationId) {
      dispatch(
        showSnackbar({
          message: "Conversation ID not found",
          autoHideDuration: 2500,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
      return;
    }
    setSelectedConversationId(conversationId);
    setDeleteConversationModal(true);
  };
  const handleCloseView = () => {
    setViewConversationModal(false);
    setSelectedConversationId("");
  };
  const handleCloseDelete = (deleted = false) => {
    setDeleteConversationModal(false);
    setSelectedConversationId("");
    if (deleted) {
      getConversations();
      getAllConversations();
    }
  };

  const handleCopy = () => {
    const header = Object.keys(formatted[0] || {}).join("\t");
    const rows = formatted.map((r) => Object.values(r).join("\t")).join("\n");
    navigator.clipboard.writeText(`${header}\n${rows}`);
  };
  const handleDownloadExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(formatted);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Conversations");
    XLSX.writeFile(workbook, "ConversationList.xlsx");
  };
  const handleDownloadCSV = () => {
    const csv = Papa.unparse(formatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "ConversationList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.autoTable({
      head: [["S.NO", "Conversation ID", "Booking ID", "Ride ID", "Booking Status", "Ride Status", "Messages Count", "Last Message", "Last Message At", "Created"]],
      body: formatted.map((r) => Object.values(r)),
      styles: { fontSize: 8 },
    });
    doc.save("ConversationList.pdf");
  };
  const handleDownloadAll = () => {
    const csv = Papa.unparse(bulkFormatted);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "All ConversationList.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: "sno", headerName: "S.No", minWidth: 70, sortable: false, disableColumnMenu: true },
    { field: "conversationId", headerName: "Conversation ID", minWidth: 220, sortable: true },
    { field: "bookingId", headerName: "Booking ID", minWidth: 220, sortable: true },
    { field: "rideId", headerName: "Ride ID", minWidth: 220, sortable: true },
    { field: "bookingStatus", headerName: "Booking Status", minWidth: 120, sortable: true },
    { field: "rideStatus", headerName: "Ride Status", minWidth: 120, sortable: true },
    { field: "messagesCount", headerName: "Messages", minWidth: 90, sortable: true },
    { field: "lastMessage", headerName: "Last Message", minWidth: 240, sortable: false },
    { field: "lastMessageAt", headerName: "Last Message At", minWidth: 180, sortable: true },
    { field: "createdAt", headerName: "Created", minWidth: 180, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 120,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <div className={classes.iconsWrapper}>
          <Button variant="contained" color="primary" size="small" style={{ minWidth: 36, padding: "4px 8px" }} onClick={() => handleView(params?.row?.raw)}>
            <Visibility fontSize="small" />
          </Button>
          <Button variant="contained" color="error" size="small" style={{ minWidth: 36, padding: "4px 8px", backgroundColor: "#e5393526" }} onClick={() => handleDelete(params?.row?.raw)}>
            <Delete fontSize="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Grid container className={classes.root}>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<ContentCopyOutlined />} onClick={handleCopy}>COPY</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<TableChartOutlined />} onClick={handleDownloadExcel}>EXCEL</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<DescriptionOutlined />} onClick={handleDownloadCSV}>CSV</Button></Grid>
      <Grid item xs={1} md={1} className={classes.funBtn}><Button variant="contained" startIcon={<PictureAsPdfOutlined />} onClick={handleDownloadPDF}>PDF</Button></Grid>
      <Grid item xs={2} md={2} className={classes.funBtn}><Button variant="contained" startIcon={<FileDownloadOutlined />} onClick={handleDownloadAll}>Download ALL</Button></Grid>
      <Grid item xs={6} md={6} className={classes.addBtn}></Grid>

      <Grid item xs={12}>
        <section className={classes.dataGridTable}>
          <DataGrid
            rows={dataRows}
            columns={columns}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            onPageChange={(newPage) => {
              const p = parseInt(newPage, 10) + 1;
              if (p >= 1) setPage(p);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            pagination
            autoHeight
            disableSelectionOnClick
            scrollbarSize={4}
            rowHeight={60}
            style={{ padding: "10px", borderRadius: "0px", overflowX: "auto" }}
            paginationMode="server"
            rowCount={totalPages}
            filterMode="server"
            onFilterModelChange={(model) => setSearchText(model?.quickFilterValues?.join(" ") || "")}
            density="compact"
            components={{ Toolbar: CustomToolbar }}
          />
        </section>
      </Grid>
      <ConversationViewModal openDialog={viewConversationModal} handleClose={handleCloseView} conversationId={selectedConversationId} />
      <ConversationDeleteModal openDialog={deleteConversationModal} handleClose={handleCloseDelete} conversationId={selectedConversationId} />
    </Grid>
  );
};

export default Conversation;
